const API_BASE = '/api'

export interface CreditInfo {
  creditId: string
  source: 'free' | 'one_time' | 'subscription'
  status: 'available' | 'used' | 'expired'
  grantedAt: string
  expiresAt: string
  usedForFormId: string
}

export interface CreditSummary {
  credits: CreditInfo[]
  summary: {
    available: number
    used: number
    expired: number
  }
}

export interface ActivationCheck {
  isActivated: boolean
  activatedBy: string | null
  canActivate: boolean
  availableCredits: number
}

// Get user's credits
export async function getCredits(): Promise<CreditSummary> {
  const response = await fetch(`${API_BASE}/credits`, {
    credentials: 'include',
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get credits')
  }
  return response.json()
}

// Activate a form using a credit
export async function activateForm(formId: string): Promise<{ success: boolean; creditId?: string }> {
  const response = await fetch(`${API_BASE}/credits/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ formId }),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to activate form')
  }
  return response.json()
}

// Check form activation status
export async function checkActivation(formId: string): Promise<ActivationCheck> {
  const response = await fetch(`${API_BASE}/credits/check/${formId}`, {
    credentials: 'include',
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to check activation')
  }
  return response.json()
}

// Get Endorsely referral ID if present
function getEndorselyReferral(): string {
  return (window as any).endorsely_referral || ''
}

// Create one-time payment checkout
export async function createOneTimeCheckout(formId: string): Promise<{ checkoutUrl: string }> {
  const response = await fetch(`${API_BASE}/payments/checkout/one-time`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ formId, endorselyReferral: getEndorselyReferral() }),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create checkout')
  }
  return response.json()
}

// Create subscription checkout
export async function createSubscriptionCheckout(planId: 'plan_35' | 'plan_100'): Promise<{ checkoutUrl: string }> {
  const response = await fetch(`${API_BASE}/payments/checkout/subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ planId, endorselyReferral: getEndorselyReferral() }),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create checkout')
  }
  return response.json()
}

// Open Stripe customer portal
export async function createPortalSession(): Promise<{ portalUrl: string }> {
  const response = await fetch(`${API_BASE}/payments/portal`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to open billing portal')
  }
  return response.json()
}
